import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send, Trash2, X, CheckCircle2, Zap,
  Loader, Bot, User, AlertCircle, Sparkles
} from 'lucide-react';
import { aiAssistantAPI } from '../services/api';

const STORAGE_KEY = 'reactorhub_ai_chat';

const AdminAIChat = ({ colors, isExpanded, onToggle }) => {
  // Load initial state from localStorage
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        return data.messages || [];
      }
    } catch (e) {
      console.error('Error loading chat history:', e);
    }
    return [];
  });

  const [sessionId] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        if (data.sessionId) return data.sessionId;
      }
    } catch (e) {}
    return `admin-${Date.now()}`;
  });

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionsPerformed, setActionsPerformed] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Save to localStorage whenever messages change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages,
        sessionId,
        lastUpdated: Date.now()
      }));
    } catch (e) {
      console.error('Error saving chat history:', e);
    }
  }, [messages, sessionId]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isExpanded]);

  // Add welcome message on first expand (only if no existing messages)
  useEffect(() => {
    if (isExpanded && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: "Hello! I'm your AI assistant for ReactorHub admin. I can **actually perform actions** like:\n\n- **Add channels** - Give me a YouTube channel ID and I'll add it\n- **Ingest videos** - I'll fetch and save videos from channels\n- **Moderation** - Approve/reject peak moments and media\n- **Search** - Find channels, media, forum threads\n- **Stats** - Get database statistics and insights\n\nTry: \"Add channel UCeFKmd71jJst-sZzuqcqFrA and ingest its videos\"",
        timestamp: new Date()
      }]);
    }
  }, [isExpanded, messages.length]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isExpanded) {
        onToggle();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isExpanded, onToggle]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);
    setActionsPerformed([]);

    try {
      const response = await aiAssistantAPI.chat(userMessage.content, sessionId);

      if (response.success) {
        // Track actions that were performed
        if (response.actionsPerformed && response.actionsPerformed.length > 0) {
          setActionsPerformed(response.actionsPerformed);
        }

        setMessages(prev => [...prev, {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.response,
          actions: response.actionsPerformed || [],
          timestamp: new Date()
        }]);
      } else {
        throw new Error(response.error || 'Failed to get response');
      }
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to send message');
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'error',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await aiAssistantAPI.clearHistory(sessionId);
      const newMessages = [{
        id: 'welcome-new',
        role: 'assistant',
        content: "Conversation cleared. How can I help you?",
        timestamp: new Date()
      }];
      setMessages(newMessages);
      setError(null);
      setActionsPerformed([]);
      // Clear localStorage
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error('Clear error:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Format message content with basic markdown
  const formatMessage = (content) => {
    const parts = content.split(/(```[\s\S]*?```)/g);

    return parts.map((part, index) => {
      if (part.startsWith('```')) {
        const code = part.replace(/```\w*\n?/g, '').replace(/```$/g, '');
        return (
          <pre key={index} style={{
            backgroundColor: colors.bg,
            padding: '12px',
            borderRadius: '8px',
            overflow: 'auto',
            fontSize: '13px',
            margin: '8px 0',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            <code>{code}</code>
          </pre>
        );
      }

      return part.split('\n').map((line, lineIndex) => {
        let formatted = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        if (formatted.startsWith('- ')) {
          formatted = `<span style="margin-left: 12px; display: block;">${formatted}</span>`;
        }

        return (
          <span key={`${index}-${lineIndex}`}>
            <span dangerouslySetInnerHTML={{ __html: formatted }} />
            {lineIndex < part.split('\n').length - 1 && <br />}
          </span>
        );
      });
    });
  };

  // Collapsed view - button in sidebar
  if (!isExpanded) {
    return (
      <div
        onClick={onToggle}
        style={{
          margin: '16px 12px',
          padding: '14px 16px',
          backgroundColor: colors.bgTertiary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colors.accent + '20';
          e.currentTarget.style.borderColor = colors.accent;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = colors.bgTertiary;
          e.currentTarget.style.borderColor = colors.border;
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Sparkles size={20} style={{ color: colors.accent }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
              AI Assistant
            </div>
            <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
              Click to open chat
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Expanded view - FLOATING PANEL over the dashboard
  return (
    <>
      {/* Backdrop overlay */}
      <div
        onClick={onToggle}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998
        }}
      />

      {/* Floating chat panel */}
      <div style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '450px',
        height: '600px',
        backgroundColor: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9999,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: colors.bgTertiary
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${colors.accent} 0%, #EC4899 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Sparkles size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: colors.text }}>
                AI Assistant
              </div>
              <div style={{ fontSize: '11px', color: colors.textSecondary }}>
                Powered by Gemini 2.5 Flash
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={clearChat}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                color: colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bg}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Clear chat"
            >
              <Trash2 size={18} />
            </button>
            <button
              onClick={onToggle}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                color: colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bg}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Close (ESC)"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {messages.map((message) => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
              }}
            >
              {/* Avatar */}
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: message.role === 'user' ? colors.accent :
                                message.role === 'error' ? colors.error : colors.info,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {message.role === 'user' ? (
                  <User size={16} color="#fff" />
                ) : message.role === 'error' ? (
                  <AlertCircle size={16} color="#fff" />
                ) : (
                  <Bot size={16} color="#fff" />
                )}
              </div>

              {/* Message Bubble */}
              <div style={{ maxWidth: '80%' }}>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: message.role === 'user' ? colors.accent :
                                  message.role === 'error' ? colors.error + '20' : colors.bg,
                  color: message.role === 'user' ? '#fff' :
                        message.role === 'error' ? colors.error : colors.text,
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  {formatMessage(message.content)}
                </div>

                {/* Show actions performed */}
                {message.actions && message.actions.length > 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: colors.success + '15',
                    borderRadius: '8px',
                    border: `1px solid ${colors.success}30`
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: colors.success,
                      marginBottom: '4px'
                    }}>
                      <Zap size={12} />
                      Actions Executed:
                    </div>
                    {message.actions.map((action, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        color: colors.textSecondary,
                        marginTop: '2px'
                      }}>
                        <CheckCircle2 size={10} style={{ color: colors.success }} />
                        {action.name}
                        {action.result?.success === false && (
                          <span style={{ color: colors.error }}>(failed)</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: colors.info,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Bot size={16} color="#fff" />
              </div>
              <div style={{
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: colors.bg,
                color: colors.textSecondary,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Thinking...
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error Banner */}
        {error && (
          <div style={{
            padding: '12px 20px',
            backgroundColor: colors.error + '20',
            color: colors.error,
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        {/* Input Area */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${colors.border}`,
          display: 'flex',
          gap: '12px',
          backgroundColor: colors.bgTertiary
        }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything about managing ReactorHub..."
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bg,
              color: colors.text,
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = colors.accent}
            onBlur={(e) => e.target.style.borderColor = colors.border}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            style={{
              padding: '12px 16px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: input.trim() && !isLoading ? colors.accent : colors.border,
              color: input.trim() && !isLoading ? '#fff' : colors.textSecondary,
              cursor: input.trim() && !isLoading ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <Send size={18} />
          </button>
        </div>
      </div>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default AdminAIChat;
